import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  salesPeriods,
  salesScopes,
  type ControllingSales,
  type SalesPeriod,
  type SalesScope,
} from '@brimo/shared';
import { ApiError, api } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { downloadCsv } from '../figures/api';

/** `GET /controlling/sales` and its CSV export (docs/dev/api-controlling.md § Vertrieb, D-323). */

export type { ControllingSales, SalesPeriod, SalesScope };

export function isSalesPeriod(value: string | null): value is SalesPeriod {
  return (salesPeriods as readonly string[]).includes(value ?? '');
}

export function isSalesScope(value: string | null): value is SalesScope {
  return (salesScopes as readonly string[]).includes(value ?? '');
}

export interface SalesFilter {
  period: SalesPeriod;
  /** null: the server picks the widest scope of the viewer. */
  scope: SalesScope | null;
  /** One partner of the scope (row click). */
  userId: string | null;
}

export function salesQuery(filter: SalesFilter): string {
  const search = new URLSearchParams({ period: filter.period });
  if (filter.scope) search.set('scope', filter.scope);
  if (filter.userId) search.set('userId', filter.userId);
  return search.toString();
}

export const salesKey = (filter: SalesFilter) =>
  [
    ...queryRoots.controlling,
    'sales',
    filter.period,
    filter.scope ?? 'default',
    filter.userId ?? '',
  ] as const;

export function useSales(filter: SalesFilter) {
  return useQuery({
    queryKey: salesKey(filter),
    queryFn: ({ signal }) => api<ControllingSales>(`/controlling/sales?${salesQuery(filter)}`, { signal }),
    placeholderData: keepPreviousData,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 60_000,
  });
}

/** „Export": `GET /controlling/sales/export` with the page's query, saved as `vertrieb.csv`. */
export function downloadSales(filter: SalesFilter): Promise<void> {
  return downloadCsv(`/controlling/sales/export?${salesQuery(filter)}`, 'vertrieb.csv');
}
